import React from "react";
import { useParams } from "react-router-dom";
import moment from "moment";
import {
  MdAddChart,
  MdDashboard,
  MdGroups,
  MdPalette,
  MdSettingsInputAntenna,
} from "react-icons/md";
import { AiOutlineFieldTime } from "react-icons/ai";
import { useAppSelector } from "../../../../app/hooks";
import { selectGroupById } from "../../../../features/groups/groupSlice";
import useGetLanguage from "../../../../utils/useGetLanguage";
import { LanguageText } from "../../../../dictionary/GroupsText";
import PerkDisplay from "./PerkDisplay";
import { isStyleFeatureAvailable } from "../utils";

const CurrentPlanCard: React.FC = () => {
  const language: LanguageText = useGetLanguage({
    fileName: "GroupsText",
  });
  const { groupid } = useParams<{ groupid: string }>();
  const groupInfo = useAppSelector((state) => selectGroupById(state, groupid ?? ""));

  if (!groupInfo?.subscription) {
    return (
      <div className="p-4 bg-gray-50 rounded-lg shadow text-center text-gray-600">
        No subscription information available.
      </div>
    );
  }

  const { plan_name, price, renewal_date, renewal_period, perks } =
    groupInfo.subscription;

  const formatDataRetention = (d: number, m: number, y: number) => {
    const parts = [];
    if (d)
      parts.push(
        `${d} ${d > 1 ? language?.groupSubscription?.days : language?.groupSubscription?.day
        }`
      );
    if (m)
      parts.push(
        `${m} ${m > 1 ? language?.groupSubscription?.months : language?.groupSubscription?.month
        }`
      );
    if (y)
      parts.push(
        `${y} ${y > 1 ? language?.groupSubscription?.years : language?.groupSubscription?.year
        }`
      );
    return parts.join(" ");
  };

  return (
    <div className="mx-auto my-4 sm:my-6 md:my-8 p-3 sm:p-4 md:p-6 bg-gray-400 border border-gray-200 rounded-lg shadow space-y-3 sm:space-y-4 md:space-y-6 w-full max-w-md overflow-hidden">
      <div className="bg-gray-50 border rounded-md p-3 sm:p-4 md:p-6 shadow flex flex-col items-center text-center w-full min-w-0">
        {plan_name && (
          <div className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 mb-1 break-words w-full">
            {plan_name}
          </div>
        )}
        <div className="text-blue-600 text-sm sm:text-base md:text-lg font-semibold">
          {price === 0 || price === undefined
            ? language?.groupSubscription?.free
            : `£${(price / 100).toFixed(2)}`}
          {price !== 0 && price !== undefined && (
            <span className="ml-1 text-gray-600 font-normal text-xs">
              / {renewal_period === "month" ? "month" : "year"}
            </span>
          )}
        </div>
        {price !== 0 && price !== undefined && renewal_date && (
          <div className="mt-1 text-xs md:text-sm text-gray-600">
            Next Billed on {moment(renewal_date).format("DD/MM/YYYY HH:mm")}
          </div>
        )}
      </div>

      {/* ---------- PERKS ---------- */}
      <div className="overflow-x-auto w-full">
        <div className="flex flex-col gap-1.5 sm:gap-2 md:gap-3 w-full">
          {perks?.users !== undefined && (
            <PerkDisplay
              icon={<MdGroups size={28} />}
              value={`${Object.keys(groupInfo.users ?? {}).length}/${perks.users}`}
              label={language?.groupSubscription?.members}
            />
          )}
          {perks?.dashboards !== undefined && (
            <PerkDisplay
              icon={<MdDashboard size={28} />}
              value={`${Object.keys(groupInfo.dashboards ?? {}).length}/${perks.dashboards
                }`}
              label={language?.groupSubscription?.dashboards}
            />
          )}
          {perks?.devices !== undefined && (
            <PerkDisplay
              icon={<MdSettingsInputAntenna size={28} />}
              value={`${Object.keys(groupInfo.devices ?? {}).length}/${perks.devices}`}
              label={language?.groupSubscription?.devices}
            />
          )}
          {perks?.data_retention && (
            <PerkDisplay
              icon={<AiOutlineFieldTime size={28} />}
              value={formatDataRetention(
                perks.data_retention.days,
                perks.data_retention.months,
                perks.data_retention.years
              )}
              label={language?.groupSubscription?.dataRetention}
            />
          )}
          {perks?.dashboard_elements !== undefined && (
            <PerkDisplay
              icon={<MdAddChart size={28} />}
              value={
                perks.dashboard_elements === -1
                  ? language?.groupSubscription?.unlimited
                  : perks.dashboard_elements
              }
              label={language?.groupSubscription?.dashboardElements}
            />
          )}
          {(isStyleFeatureAvailable(plan_name)) && (
            <PerkDisplay
              icon={<MdPalette size={28} />}
              value={language?.groupSubscription?.included ?? "Included"}
              label={language?.groupSubscription?.customStyleAndUseAtLogin ?? "Custom style & use style at login"}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default CurrentPlanCard;
